import React, { FC, ReactNode, useMemo } from 'react';
import { createRoot } from 'react-dom/client';
import { Provider } from 'react-redux';
import { ThemeProvider } from 'styled-components';
import { ConfigProvider } from 'antd';
import { BrowserRouter as Router } from 'react-router-dom';
import dayjs from 'dayjs';
import updateLocale from 'dayjs/plugin/updateLocale';
import { BrowserView, MobileView } from 'react-device-detect';
import App from './App';
import { setupStore } from './store';
import { ErrorBoundary } from '@/components';
import '@/services/localization';
import { GlobalStyles, getAntdConfig, theme, MobileStyle, ThemeModeProvider, useThemeMode } from '@/styles';

dayjs.extend(updateLocale);
dayjs.updateLocale('en', {
  weekStart: 1
});

const store = setupStore();

/** Re-derives the antd theme when the light/dark mode changes. */
const ThemedConfigProvider: FC<{ children: ReactNode }> = ({ children }) => {
  const { mode } = useThemeMode();
  const config = useMemo(() => getAntdConfig(mode), [mode]);
  return <ConfigProvider theme={config}>{children}</ConfigProvider>;
};
const root = createRoot(document.getElementById('root') as HTMLElement);

root.render(
  <>
    <BrowserView>
      <React.StrictMode>
        <Provider store={store}>
          <ThemeModeProvider>
            <ThemedConfigProvider>
              <ThemeProvider theme={theme}>
                <GlobalStyles />
                <Router>
                  <ErrorBoundary>
                    <App />
                  </ErrorBoundary>
                </Router>
              </ThemeProvider>
            </ThemedConfigProvider>
          </ThemeModeProvider>
        </Provider>
      </React.StrictMode>
    </BrowserView>
    <MobileView>
      <MobileStyle />
      <p>Please use your desktop to use the app.</p>
    </MobileView>
  </>
);
